import { Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';

// state machine:
//   'closed'           — hidden
//   'loading'          — Apollo query in-flight; disable close, show spinner
//   'ready'            — count populated, Launch enabled
//   'empty'            — Apollo returned zero results (real miss), only offer Back to Design
//   'all_existing'     — Apollo returned N but every one is already in the coach's pipeline
//                        (existingCount === Apollo total). Different remedy than 'empty' —
//                        adjust targeting for FRESH people, not just change criteria at random.
//   'credit_exhausted' — Apollo enrichment 402'd on most candidates; distinguishes credit
//                        problems from genuine "no matches" so the coach doesn't think
//                        their targeting is wrong when it's actually a billing issue.
export default function LaunchConfirmModal({
  state = 'closed',
  count = 0,
  existingCount = 0,
  confirming = false,
  onClose,
  onConfirm,
}) {
  const opened = state !== 'closed';
  const canClose = state !== 'loading' && !confirming;

  const title =
    state === 'loading'          ? 'Finding prospects…' :
    state === 'empty'            ? 'No prospects found' :
    state === 'all_existing'     ? 'Already in your pipeline' :
    state === 'credit_exhausted' ? 'Apollo credits exhausted' :
                                   'Launch campaign?';

  return (
    <Modal
      opened={opened}
      onClose={canClose ? onClose : () => {}}
      title={title}
      centered
      closeOnClickOutside={canClose}
      closeOnEscape={canClose}
      withCloseButton={canClose}
    >
      <Stack gap="md">
        {state === 'loading' && (
          <Group gap="sm" wrap="nowrap">
            <Loader size="sm" color="sage" />
            <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
              Searching for matching prospects… this can take up to 20 seconds.
            </Text>
          </Group>
        )}

        {state === 'empty' && (
          <>
            <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
              No prospects matched your criteria. Adjust the Design questions and try again.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button color="sage" onClick={onClose}>
                Back to Design
              </Button>
            </Group>
          </>
        )}

        {state === 'all_existing' && (
          <>
            <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
              We found {existingCount} {existingCount === 1 ? 'person' : 'people'} matching
              your criteria, but they're all already in your pipeline from earlier campaigns.
              Adjust the Design questions to find fresh prospects.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button color="sage" onClick={onClose}>
                Back to Design
              </Button>
            </Group>
          </>
        )}

        {state === 'credit_exhausted' && (
          <>
            <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
              Apollo enrichment credits are exhausted, so we couldn't pull contact
              details for the people we found. Top up your Apollo account, then try
              again — your Design answers are saved.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button color="sage" onClick={onClose}>
                Back to Design
              </Button>
            </Group>
          </>
        )}

        {state === 'ready' && (
          <>
            <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
              Add {count} {count === 1 ? 'prospect' : 'prospects'} to your pipeline?
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={onClose} disabled={confirming}>
                Cancel
              </Button>
              <Button color="sage" onClick={onConfirm} loading={confirming}>
                Launch
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
